import {
	Table,
	Stack,
	Button,
	Component,
	Icons,
	Text,
	Dropdown,
	Divider,
	Box,
} from "@sembark-travel/ui-base"
import {
	areAdvancedFiltersAppliedDefault,
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	CheckboxInputField,
	Form,
	isTruthy,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Optional } from "utility-types"

import { worldXHR, TWorldAirline } from "../../World"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../../Auth"

type TFilters = TSearchParams & {
	unverified_only?: 1
}

type TFiltersInQuery = TSearchParams & {
	unverified_only?: 1
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const { unverified_only, ...others } = filters
	const query: TFiltersInQuery = { ...others }
	if (isTruthy(unverified_only)) {
		query.unverified_only = 1
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const { unverified_only, ...others } = query
	const filters: TFilters = { ...others }
	if (isTruthy(unverified_only)) {
		filters.unverified_only = 1
	}
	return filters
}
function Filters() {
	return (
		<Stack>
			<SwitchInputField name="unverified_only" label="Unverified Only" />
		</Stack>
	)
}
export function AirlinesList() {
	const xhr = useXHR()
	const [query, setQuery] = useLocationQuery({
		fromQuery: queryToFilters,
		toQuery: filtersToQuery,
	})
	const [searchParams, setSearchParams] = useSearch(query)
	useEffect(() => {
		setQuery(searchParams)
	}, [searchParams, setQuery])
	return (
		<Search
			title="Airlines"
			initialParams={searchParams}
			onSearch={(params) => setSearchParams({ ...params, cursor: null })}
			Filters={Filters}
			resetParams={{
				q: "",
				page: 1,
			}}
			areAdvancedFiltersApplied={(filters) => {
				const { unverified_only, ...otherFilters } = filters
				return (
					isTruthy(unverified_only) ||
					areAdvancedFiltersAppliedDefault(otherFilters)
				)
			}}
			actions={() => (
				<Component initialState={false}>
					{({ state, setState }) => (
						<>
							<Button onClick={() => setState(true)}>New Airline</Button>
							<Dialog
								open={state}
								onClose={() => setState(false)}
								title="Add New Airline"
							>
								<Dialog.Body>
									<AirLineForm
										onCancel={() => setState(false)}
										onSubmit={async (payload) => {
											await worldXHR(xhr)
												.storeAirline(payload)
												.then(() => {
													setState(false)
													showSnackbar("Airline added successfully")
												})
										}}
									/>
								</Dialog.Body>
							</Dialog>
						</>
					)}
				</Component>
			)}
		>
			{() => (
				<CursorListView<TWorldAirline>
					pageKey="admin/world/airlines"
					params={searchParams}
					onCursorChange={(cursor) =>
						setSearchParams({ ...searchParams, cursor })
					}
					fetch={(xhr, params) =>
						worldXHR(xhr).getAirlinesWithPagination({
							...params,
						})
					}
				>
					{({ items, refresh }) => (
						<Table
							headers={["Name", "IATA", "ICAO", ""]}
							alignCols={{ 3: "right" }}
							responsive
							hover
							bordered
							rows={items.map((item) => [
								item.name,
								item.iata,
								item.icao ? item.icao : <Text color="muted">N/A</Text>,
								<Dropdown alignRight>
									<Dropdown.ToggleButton level="tertiary" size="sm">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<Component initialState={false}>
											{({ state, setState }) => (
												<>
													<Dropdown.MenuItem onClick={() => setState(true)}>
														<Icons.Pencil /> Edit
													</Dropdown.MenuItem>
													<Dialog
														open={state}
														onClose={() => setState(false)}
														title={`Edit Airline "${item.name}"`}
													>
														<Dialog.Body>
															<EditAirline
																airline={item}
																onCancel={() => setState(false)}
																onSuccess={() => {
																	refresh()
																	setState(false)
																}}
																tenantId={item.tenant_id}
															/>
														</Dialog.Body>
													</Dialog>
												</>
											)}
										</Component>
										<Dropdown.MenuItem
											onClick={() => {
												if (
													window.confirm(
														"Are you sure you want to delete this airline ?."
													)
												) {
													worldXHR(xhr)
														.destroyAirline(item.id)
														.then((resp) => {
															showSnackbar(
																resp || "Airline deleted successfully."
															)
															refresh()
														})
														.catch((e) => {
															const error: Error = e
															window.alert(
																error.message || "Something went wrong"
															)
														})
												}
											}}
										>
											<Icons.Trash /> Delete
										</Dropdown.MenuItem>
									</Dropdown.Menu>
								</Dropdown>,
							])}
						/>
					)}
				</CursorListView>
			)}
		</Search>
	)
}
interface IAirLineForm extends Omit<TWorldAirline, "tenant_id"> {
	mark_verified?: 0 | 1
}
export function EditAirline({
	airline,
	onSuccess,
	onCancel,
	tenantId,
}: {
	airline: IAirLineForm
	onSuccess: () => void
	onCancel?: () => void
	tenantId?: number
}) {
	const xhr = useXHR()
	return (
		<AirLineForm
			initialValues={airline}
			onSubmit={async (val) => {
				await worldXHR(xhr)
					.updateAirline(airline.id, val)
					.then(() => {
						onSuccess()
						showSnackbar("Airline updated successfully.")
					})
			}}
			onCancel={onCancel}
			tenantId={tenantId}
		/>
	)
}
const validationAirlineSchema = validateFormValues(
	Validator.object().shape({
		name: Validator.string().required("Airline name is required"),
		iata: Validator.string()
			.required("Airline code is required")
			.min(2, "Iata code should be 2 or 3 characters")
			.max(3, "Iata code should be 2 or 3 characters"),
	})
)
export function AirLineForm({
	initialValues: propInitialValues = {},
	onSubmit,
	onCancel,
	tenantId,
}: {
	initialValues?: Optional<IAirLineForm>
	onSubmit: (payload: object) => Promise<unknown>
	onCancel?: () => void
	tenantId?: number
}) {
	const initialValues: Optional<IAirLineForm> = useMemo(
		() => ({
			name: "",
			iata: "",
			icao: "",
			...propInitialValues,
		}),
		[propInitialValues]
	)
	const { hasPermission } = useCheckPermissions()
	const canVerify = hasPermission(PERMISSIONS.MANAGE_TENANTS)
	return (
		<Form<IAirLineForm>
			initialValues={initialValues}
			validate={validationAirlineSchema}
			onSubmit={withServerErrors(async (val) => {
				await onSubmit(val)
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="2">
						<TextInputField
							name="name"
							type="text"
							placeholder="eg. Air India"
							label={"Name"}
						/>
						<TextInputField
							name="iata"
							type="text"
							placeholder="eg. AI"
							label={"Iata Code"}
						/>
						<TextInputField
							name="icao"
							type="text"
							secondaryLabel="Optional"
							placeholder="eg. AI"
							label={"Icao Code"}
						/>
						{tenantId && canVerify ? (
							<Box>
								<CheckboxInputField
									name="mark_verified"
									label={"Mark as verified"}
									type="checkbox"
								/>
							</Box>
						) : null}
						<SubmissionError />
					</Stack>
					<Divider sm />
					<Button type="submit" disabled={submitting} level="primary">
						{submitting ? "Saving ..." : "Save"}
					</Button>
				</form>
			)}
		</Form>
	)
}
